/**
 *
 * FBDocuments
 *
 */

import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form } from "antd";

// icons
//import CustomIcons from 'components/CustomIcons';
//import { ExclamationCircleOutlined } from '@ant-design/icons';

// components
import ErrorBoundary from "engine/ErrorBoundary";
import HiddenField from "components/Forms/Fields/HiddenField";

// Communication components
import {
  FBSectionHeader,
  FBSectionErroMessage,
} from "components/Communications";
// inner components
import FileRow from "./components/FileRow";
import UploadFileButton from "./components/UploadFileButton";

// hooks
import api from "utils/api";
import { useRefState } from "utils/hooks";
import { slugify, truncateFilename } from "utils/helpers/helper.general";

function validateForm(formData, name = "documents") {
  let isValid = true;
  if (formData?.[`${name}-fields`]?.length > 0) {
    for (let field of formData?.[`${name}-fields`]) {
      if (!!!field?.["file-name"]) {
        isValid = false;
      } else if (!!!field?.["file-url"]) {
        isValid = false;
      }
    }
  } else {
    isValid = false;
  }
  return isValid;
}

function FBDocuments({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    "tcf-fb-documents-group",
    "tcf-fb-group",
    className,
  );
  const { t, i18n } = useTranslation();

  const [docsForm] = Form.useForm();

  const [validAmountOfFields, setValidAmountOfFields] = useState(
    isValidSection || false,
  );
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeAll, setRemoveAll] = useState(true);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(
    initialValues || {},
  );

  useEffect(() => {
    if (!!initialValues) {
      const isValid = validateForm(initialValues);
      setValidAmountOfFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  useEffect(() => {
    if (!removeAll) {
      resetAllStates();
      !!onChangeSection &&
        onChangeSection(name, null);
      !!onDelete && onDelete(name);
    }
    return () => {
      setRemoveAll(true);
    };
  }, [removeAll]);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidAmountOfFields(false);
    docsForm.resetFields();
  }, [initialValues, currentValues]);

  // Delete Section
  const deleteSection = name => {
    const mediaFiles = currentValues?.[`${name}-fields`];
    if (mediaFiles?.length > 0) {
      let deletedAmpount = 0;
      for (const file of mediaFiles) {
        if (!!file?.["file-url"]) {
          fileOnRemove(file?.["file-url"]);
          deletedAmpount++;
        }
      }
      if (mediaFiles?.length == deletedAmpount) {
        setRemoveAll(false);
      }
    } else {
      setRemoveAll(false);
    }
  };

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
      const isValid =
        validateForm(currentValuesRef?.curent || currentValues) &&
        values?.length > 0;
      setValidAmountOfFields(isValid);
      !!setValidSection && setValidSection(name, isValid);

      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(new Error(t("errors.entryNotValid")), values);
      }
    },
    [setValidSection, currentValues, currentValuesRef],
  );

  // Add row
  const onAddNewMedia = useCallback(
    (data, callbackAdd) => {
      if (data?.length > 0) {
        const fileUrl = data[0];
        const fileName = truncateFilename(fileUrl);
        const name = fileName.split('.')[0];
        const newValue = {
          "file-name": slugify(name),
          "file-url": fileUrl,
        };

        const filedsValues = currentValues[`${name}-fields`] || [];
        const hasNewData =
          filedsValues?.length > 0 &&
          filedsValues?.some(file => file["file-url"] == fileUrl);

        if (!!!hasNewData || filedsValues?.length == 0) {
          !!callbackAdd && callbackAdd(newValue);
          setValidAmountOfFields(true);
        }
      }
    },
    [currentValues],
  );

  const fileOnRemove = async file => {
    setRemoveLoading(true);
    try {
      if (typeof file === "string") {
        await api.media.delete(file);
      }
      setRemoveLoading(false);
    } catch (err) {
      setRemoveLoading(false);
      console.error(err);
    }
  };

  // Delete Row
  const onRemoveRow = useCallback(
    (index, callbackRemove) => {
      let values = currentValues || {};
      let filedsValues = currentValues[`${name}-fields`] || [];

      if (filedsValues?.length > 0) {
        let currentFile = filedsValues?.find((el, inx) => inx == index);
        currentFile = currentFile?.["file-url"] || "";

        if (!!currentFile) {
          fileOnRemove(currentFile);
        }

        filedsValues =
          filedsValues?.filter((el, inx) => inx != index) || filedsValues;
        values = {
          ...values,
          [`${name}-fields`]: filedsValues,
        };
        setCurrentValues(values);
        docsForm.setFieldsValue(values);
        if (filedsValues?.length == 0) {
          setValidAmountOfFields(false);
        }
        !!callbackRemove && callbackRemove(index);
      } else {
        values = {
          ...values,
          [`${name}-fields`]: [],
        };
        setCurrentValues(values);
        docsForm.setFieldsValue(values);
        setValidAmountOfFields(false);
      }
    },
    [currentValues, currentValuesRef, docsForm],
  );

  const erroMessage = useMemo(
    () => (
      <FBSectionErroMessage
        showMessage={!validAmountOfFields}
        message={t("inputs.sectionNeedsMultipleFiles")}
      />
    ),
    [validAmountOfFields, isValidSection, i18n?.language],
  );

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      let values = allValues[name] || {};
      const isValid = validateForm(values);
      setValidAmountOfFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      !!isValid && setCurrentValues(values);
      // docsForm.validateFields();
    },
    [name, currentValues, setValidSection],
  );

  const renderFormList = useCallback((fields, { add, remove, move }) => {
    return (
      <>
        {fields?.map((element, index) => {
          return (
            <FileRow
              key={`${name}-fields-` + index}
              nameRow={`${name}-fields`}
              element={element}
              rowsValues={currentValues[`${name}-fields`] || []}
              onRemove={name => onRemoveRow(name, remove)}
            />
          );
        })}

        <UploadFileButton
          onAdd={data => onAddNewMedia(data, add)}
        />
      </>
    );
  }, [currentValues]);

  return (
    name && (
      <div className={childClassNames}>
        <Form
          name={name}
          form={docsForm}
          initialValues={{ [name]: currentValues }}
          className="tcf-form-builder"
          autoComplete="off"
          onValuesChange={handleValuesChange}
          loading={!!removeLoading}
        >
          <FBSectionHeader
            type="documents"
            loading={!!removeLoading || (!!removeLoading && !!removeAll)}
            onDelete={() => deleteSection(name)}
          />
          <div className="tcf-fb-group">
            <HiddenField
              key={[name, `${name}-title`]}
              data={{ name: [name, `${name}-title`] }}
              index={0}
            />
            <HiddenField
              key={[name, `${name}-description`]}
              data={{ name: [name, `${name}-description`] }}
              index={0}
            />

            <ErrorBoundary>
              <Form.List
                className="tcf-fb-group"
                name={[name, `${name}-fields`]}
                //  validateTrigger="onChange"
                rules={[
                  {
                    required: true,
                    validator: checkValidCallback,
                    validateTrigger: "onChange",
                    message: t("inputs.mandatoryField"),
                  },
                ]}
              >
                {(fields, { add, remove, move }) => renderFormList(fields, { add, remove, move })}
              </Form.List>
            </ErrorBoundary>

            {erroMessage}
          </div>
        </Form>
      </div>
    )
  );
}

FBDocuments.defaultProps = {
  name: "documents",
  mode: "new",
  method: "new",
};
FBDocuments.propTypes = {
  onDelete: PropTypes.func,
  onChangeSection: PropTypes.func,
};

export default memo(FBDocuments);
